import { useState, useEffect, useRef } from 'react';
import { CATEGORICAL, INK } from './palette';

// Vertical bar chart - grows in on mount, each bar "pops" (lifts + glows)
// when clicked/tapped, toggling a pinned tooltip with the exact value.
// Direct value labels are always visible above each bar (relief rule -
// several palette slots don't clear 3:1 contrast on their own).
export default function AnimatedBarChart({ data, valueKey = 'value', labelKey = 'label', height = 220, formatValue = (v) => v, onSelect }) {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setActive(null); };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  if (!data || data.length === 0) {
    return <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 12, color: INK.muted }}>داده‌ای برای نمایش نیست</div>;
  }

  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  const barAreaHeight = height - 44; // leaves room for value label + axis label

  const select = (i) => {
    const next = active === i ? null : i;
    setActive(next);
    onSelect?.(next === null ? null : data[next]);
  };

  return (
    <div ref={rootRef} style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height, padding: '8px 4px 0' }}>
      {data.map((d, i) => {
        const color = CATEGORICAL[i % CATEGORICAL.length];
        const value = d[valueKey] || 0;
        const barHeight = mounted ? Math.max((value / max) * barAreaHeight, 2) : 0;
        const isActive = active === i;
        return (
          <div key={d[labelKey] ?? i} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? color : INK.secondary, transition: 'color 0.15s' }}>
              {formatValue(value)}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); select(i); }}
              title={`${d[labelKey]}: ${formatValue(value)}`}
              aria-label={`${d[labelKey]}: ${formatValue(value)}`}
              style={{
                width: '100%', maxWidth: 36, height: barHeight,
                background: color, border: 'none', borderRadius: '4px 4px 0 0',
                cursor: 'pointer', padding: 0,
                transform: isActive ? 'translateY(-6px) scaleX(1.08)' : 'translateY(0) scaleX(1)',
                boxShadow: isActive ? `0 6px 14px ${color}66` : 'none',
                transition: 'height 0.5s cubic-bezier(.22,1,.36,1), transform 0.18s ease, box-shadow 0.18s ease',
              }}
            />
            <span style={{ fontSize: 10, color: INK.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 48 }}>
              {d[labelKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}
